import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { initFlowbite } from "flowbite";
import { KeycloakService } from "keycloak-angular";
import { filter, Subscription } from "rxjs";
import { LayoutModule } from "./layout/layout.module";
import { LoaderComponent } from "./shared/loader/component/loader/loader.component";
import { AttributeCreatePanelComponent } from "./components/contact/attribute-create-panel/attribute-create-panel.component";
import { AttributePanelService } from "./services/attribute-panel/attribute-panel.service";
import { DeviseManagePanelComponent } from "./components/referential/devise-manage-panel/devise-manage-panel.component";
import { DevisePanelService } from "./services/devise-panel/devise-panel.service";
import { AnnouncementLauncherComponent } from "./shared/announcement-modal/announcement-launcher.component";
import { TrialBannerComponent } from "./layout/trial-banner/trial-banner.component";
import { v4 as uuidv4 } from "uuid";
import { PushMobileTokenApiService } from "./services/generated/core-api/services/push-mobile-token-api.service";
import { SaveFcmToken$Params } from "./services/generated/core-api/fn/push-mobile-token-api/save-fcm-token";
import { AngularFireMessaging } from "@angular/fire/compat/messaging";
import { TranslateService } from "@ngx-translate/core";
import { LanguageLayoutService } from "./shared/services/language-layout.service";
import { APP_PATHS } from "./shared/utils/app-route-paths";
import { isStatusHost } from "./shared/utils/status-host";
import { RestaurantHealthPanelComponent } from "./features/restaurant-health/restaurant-health-panel/restaurant-health-panel.component";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [
    CommonModule,
    LayoutModule,
    RouterOutlet,
    LoaderComponent,
    AttributeCreatePanelComponent,
    DeviseManagePanelComponent,
    AnnouncementLauncherComponent,
    TrialBannerComponent,
    RestaurantHealthPanelComponent,
  ],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild("mainScroll") mainScroll?: ElementRef<HTMLElement>;

  private afMessaging = inject(AngularFireMessaging);
  private router = inject(Router);
  private languageLayout = inject(LanguageLayoutService);
  private messageSubscription?: Subscription;

  title = "backoffice-ic-front";
  routeReady = false;
  isPublic = false;
  // The developer documentation portal brings its own shell and is readable without a
  // session, so it renders bare — no sidebar, no header, no authenticated bootstrap.
  isDocs = false;
  isNotFoundRoute = false;
  isTeamInbox = false;
  /** The chatbot builder takes the whole width: the backoffice sidebar is hidden there. */
  isWorkflowBuilder = false;
  isContactList = false;
  isContactUpload = false;
  isContactConsult = false;
  isTicketsHub = false;
  isTicketWorkspace = false;
  isOrdersHub = false;
  isDeliveryHub = false;
  isVoucherHub = false;
  isProductList = false;
  isPaymentList = false;
  isProductForm = false;
  isProductView = false;
  isProductUpload = false;
  isProductCrm = false;
  isProductCategory = false;
  isReservationHub = false;
  isTeamSettingsHub = false;
  isGeneralParamsHub = false;
  isMetaCatalogHub = false;
  isCatalogSetupHub = false;
  isAuditHub = false;
  isBroadcastHub = false;
  isTemplateHub = false;
  isChatBotHub = false;
  isAutomationsHub = false;
  isFlowList = false;
  isFlowCreate = false;
  isAiAgentsHub = false;
  isAiAssistanceList = false;
  isSolutionHub = false;
  attributePanelOpen = false;
  devisePanelOpen = false;
  private hasInitialized = false;
  private attributePanelSub?: Subscription;
  private devisePanelSub?: Subscription;

  constructor(
    private pushMobileTokenService: PushMobileTokenApiService,
    private translateService: TranslateService,
    private attributePanel: AttributePanelService,
    private devisePanel: DevisePanelService,
  ) {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => {
        const url = event.urlAfterRedirects;
        const routePath = url.split("?")[0].split("#")[0];
        // The public status domain has no back-office shell, session or notifications.
        this.isPublic = url.startsWith("/public") || isStatusHost();
        this.isDocs = url.startsWith("/docs");
        this.isNotFoundRoute = routePath === APP_PATHS.notFound;
        this.isTeamInbox = routePath === APP_PATHS.messaging.inbox;
        this.isWorkflowBuilder = /^\/workflow\/bots\/[^/]+\/workflow\/?$/.test(routePath);
        this.isContactList = routePath === APP_PATHS.messaging.contacts;
        this.isContactUpload = routePath === APP_PATHS.messaging.contactImport;
        this.isContactConsult =
          routePath.startsWith(`${APP_PATHS.messaging.contacts}/`) &&
          !this.isContactUpload;
        this.isTicketsHub = routePath.startsWith(APP_PATHS.messaging.tickets);
        this.isTicketWorkspace =
          this.isTicketsHub && routePath !== APP_PATHS.messaging.tickets;
        this.isOrdersHub = routePath.startsWith(APP_PATHS.catalog.orders);
        this.isDeliveryHub = routePath.startsWith(APP_PATHS.catalog.delivery);
        this.isVoucherHub = routePath.startsWith(APP_PATHS.catalog.vouchers);
        this.isProductList =
          routePath === APP_PATHS.catalog.products ||
          routePath === APP_PATHS.catalog.productArchived;
        this.isProductForm =
          routePath === APP_PATHS.catalog.productNew ||
          /\/catalog\/products\/[^/]+\/edit$/.test(routePath);
        this.isProductView =
          /\/catalog\/products\/[^/]+$/.test(routePath) && !this.isProductList;
        this.isProductUpload = routePath === APP_PATHS.catalog.productImport;
        this.isProductCrm = routePath.startsWith(
          APP_PATHS.settings.catalogIntegration,
        );
        this.isProductCategory = routePath.startsWith(
          APP_PATHS.catalog.categories,
        );
        this.isReservationHub = routePath.startsWith(
          APP_PATHS.catalog.reservations,
        );
        this.isPaymentList =
          /^\/payment\/(links|authorizations|saved-cards)$/.test(routePath);
        this.isTeamSettingsHub = routePath.startsWith("/settings/team/");
        this.isGeneralParamsHub = routePath === APP_PATHS.settings.company;
        // Legacy Meta/Catalog pages redirect into General Params — no special chrome.
        this.isMetaCatalogHub = false;
        this.isCatalogSetupHub = false;
        this.isAuditHub = routePath === APP_PATHS.settings.audit;
        this.isTemplateHub =
          routePath.startsWith(APP_PATHS.workflow.templates) ||
          routePath.startsWith(APP_PATHS.workflow.forms);
        this.isChatBotHub = routePath.startsWith(APP_PATHS.workflow.bots);
        this.isAutomationsHub =
          routePath.startsWith("/workflow/automations/") ||
          routePath === APP_PATHS.settings.notifications;
        this.isFlowList = routePath === APP_PATHS.workflow.automationFlows;
        this.isFlowCreate = routePath === APP_PATHS.workflow.automationFlowNew;
        this.isAiAgentsHub = routePath === APP_PATHS.workflow.aiAgents;
        this.isAiAssistanceList = routePath === APP_PATHS.workflow.aiAssistance;
        this.isBroadcastHub = routePath.startsWith("/campaigns");
        this.isSolutionHub = routePath.startsWith("/platform");
        this.routeReady = true;
        this.resetScrollPosition();

        if (!this.hasInitialized && !this.isPublic && !this.isDocs) {
          this.hasInitialized = true;
          this.initializeApp();
        }
      });
  }

  async ngOnInit(): Promise<void> {
    initFlowbite();
    if ("scrollRestoration" in history) {
      history.scrollRestoration = "manual";
    }
    this.attributePanelSub = this.attributePanel.open$.subscribe((open) => {
      this.attributePanelOpen = open;
    });
    this.devisePanelSub = this.devisePanel.open$.subscribe((open) => {
      this.devisePanelOpen = open;
    });
    // Clarity.init(environment.clarityProjectId);
  }

  ngOnDestroy(): void {
    this.attributePanelSub?.unsubscribe();
    this.devisePanelSub?.unsubscribe();
    this.messageSubscription?.unsubscribe();
  }

  closeAttributePanel(): void {
    this.attributePanel.close();
  }

  onAttributeSaved(): void {
    // Keep the panel open; the panel refreshes and returns to its list after save.
  }

  closeDevisePanel(): void {
    this.devisePanel.close();
  }

  onDeviseSaved(): void {
    // keep panel open on save so the user can manage multiple currencies
  }

  private resetScrollPosition(): void {
    const reset = () => {
      const scrollHost = this.mainScroll?.nativeElement;
      if (scrollHost) {
        scrollHost.scrollTop = 0;
        scrollHost.scrollLeft = 0;
      }
      document
        .querySelectorAll<HTMLElement>(
          ".main-scroll, .page-content-area, .page-content-flush, .main-content",
        )
        .forEach((element) => {
          element.scrollTop = 0;
          element.scrollLeft = 0;
        });
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    };

    reset();
    requestAnimationFrame(reset);
    setTimeout(reset, 0);
  }

  private async initializeApp(): Promise<void> {
    this.requestPermission();
    this.listenForMessages();
    const sendTranslations = () => {
      this.translateService
        .getTranslation(this.translateService.currentLang || "fr")
        .subscribe((data) => {
          if (navigator.serviceWorker && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: "translations",
              lang: this.translateService.currentLang || "fr",
              data: data,
            });
          }
        });
    };
    sendTranslations();
    this.translateService.onLangChange.subscribe(sendTranslations);
  }

  requestPermission(): void {
    Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        this.afMessaging.requestToken.subscribe({
          next: (token) => {
            this.sendTokenToServer(token);
          },
          error: (error) => {
            console.error("Erreur lors de la récupération du token", error);
          },
        });
      }
    });
  }

  sendTokenToServer(token: string | null): void {
    if (token == null) return;
    const currentUrl = this.router.url;
    const isPublicRoute =
      currentUrl.startsWith("/public") ||
      currentUrl.includes("/public/") ||
      currentUrl.startsWith("/docs");
    if (isPublicRoute) return;

    const saveFcmToken: SaveFcmToken$Params = {
      "x-api-canal": "web",
      "x-api-requestId": uuidv4(),
      body: { fcmToken: token },
    };
    this.pushMobileTokenService.saveFcmToken(saveFcmToken).subscribe({
      next: (response) => {
        //
      },
    });
  }

  private listenForMessages(): void {
    this.messageSubscription = this.afMessaging.messages.subscribe({
      next: (payload) => {
        // Mark as handled
        if (!payload.data) payload.data = {};
        payload.data["handled"] = "true";
        // Process notification if diff to /team-inbox/page
        if (this.router.url !== APP_PATHS.messaging.inbox) {
          this.showForegroundNotification(
            payload.data["title"] ||
              this.translateService.instant(
                "NOTIFICATION.NOTIFICATION_MSG.TITLE",
              ),
            payload.data["body"] ||
              this.translateService.instant(
                "NOTIFICATION.NOTIFICATION_MSG.BODY",
              ),
          );
        }
      },
    });
  }

  private showForegroundNotification(title: string, body: string): void {
    if (Notification.permission !== "granted") return;
    const options: NotificationOptions = {
      body: this.formatNotifMessage(body),
      icon: "/icons/icon-192x192.png",
      data: {
        url: "/notifications",
        timestamp: Date.now(),
      },
      badge: "/favicon.ico",
    };
    const notification = new Notification(title, options);
    // Handle click event
    notification.onclick = (event) => {
      event.preventDefault();
      window.focus();
      this.router.navigateByUrl(APP_PATHS.messaging.inbox);
      notification.close();
    };
    setTimeout(() => notification.close(), 10000);
  }

  formatNotifMessage(msg: string | undefined): string {
    if (!msg)
      return this.translateService.instant(
        "NOTIFICATION.NOTIFICATION_MSG.BODY",
      );
    const msgArr = msg.split(":");
    const type = msgArr.at(0)?.trim();
    const supportedTypes = [
      "IMAGE",
      "AUDIO",
      "VIDEO",
      "DOCUMENT",
      "INTERACTIVE",
      "ORDER",
      "CONTACTS",
      "LOCATION",
    ];
    if (!type || !supportedTypes.includes(type)) return msg;
    const icons: { [key: string]: string } = {
      IMAGE: "🖼️",
      AUDIO: "🎙️",
      VIDEO: "🎥",
      DOCUMENT: "📄",
      INTERACTIVE: "📲",
      ORDER: "🛒",
      CONTACTS: "👤",
      LOCATION: "📍",
    };
    const label =
      this.translateService.instant(`NOTIFICATION.NOTIFICATION_MSG.${type}`) ||
      type;
    return `${icons[type]} ${label}`;
  }
}
